import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Mail, ArrowRight } from "lucide-react";
import api from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [booking, setBooking] = useState(null);
  const [kind, setKind] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setBooking(data.booking);
          setKind(data.kind);
          setStatus("paid");
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus("failed");
          return;
        }
      } catch (e) { if (process.env.NODE_ENV === "development") console.error("Payment status poll failed, retrying:", e); }
      tries += 1;
      if (tries < 12) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center px-5 py-20 text-center">
      {status === "checking" && (
        <>
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <h1 className="mt-6 font-heading text-2xl font-bold">Confirming your payment…</h1>
          <p className="mt-2 text-muted-foreground">Hang tight, this only takes a moment.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent">
            <CheckCircle2 className="h-10 w-10 text-primary" />
          </span>
          <h1 className="mt-6 font-heading text-3xl font-extrabold tracking-tight">{kind === "reschedule_fee" ? "Reschedule confirmed! 🎉" : "You're all booked! 🎉"}</h1>
          <p className="mt-3 text-muted-foreground">
            {kind === "reschedule_fee"
              ? (booking ? `Your visit is moved to ${booking.slot_date} · ${booking.slot_label}. The $10 reschedule fee has been paid.` : "Your visit has been rescheduled and the $10 fee is paid.")
              : (booking ? `Thanks ${booking.name}! Your ${booking.plan_name} is confirmed for ${booking.slot_date} · ${booking.slot_label}.` : "Your cleaning is confirmed.")}
          </p>
          <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
            <Mail className="h-4 w-4 text-primary" /> A confirmation & receipt is on its way to your inbox.
          </p>
          <Link to="/" className="mt-8 inline-flex items-center gap-2 rounded-xl bg-primary px-7 py-3.5 font-bold text-primary-foreground transition-all hover:-translate-y-0.5 hover:brightness-110">
            Back Home <ArrowRight className="h-5 w-5" />
          </Link>
        </>
      )}
      {(status === "failed" || status === "error" || status === "timeout") && (
        <>
          <h1 className="font-heading text-2xl font-bold">We couldn't confirm your payment</h1>
          <p className="mt-2 text-muted-foreground">
            If you were charged, you'll still get a confirmation email. Otherwise, please try booking again.
          </p>
          <Link to="/booking" className="mt-8 inline-flex rounded-xl bg-primary px-7 py-3.5 font-bold text-primary-foreground">
            Try Again
          </Link>
        </>
      )}
    </div>
  );
}
